import { ThemeColors, useColors } from "@/constants/theme";
import { isSupabaseConfigured } from "@/lib/supabase";
import { useAppStore } from "@/stores/useStore";
import { Visibility } from "@/types";
import * as ImagePicker from "expo-image-picker";
import { Link, useLocalSearchParams, useRouter } from "expo-router";
import { Check, ImagePlus, X } from "lucide-react-native";
import { useEffect, useMemo, useState } from "react";
import { Alert, Image, Pressable, ScrollView, StyleSheet, View } from "react-native";
import { Button, Text, TextInput } from "react-native-paper";

const DISCOVERY_TYPES = [
  "日常の発見",
  "買い物の工夫",
  "街の観察",
  "言葉の発見",
  "探究メモ",
];

const VISIBILITY_OPTIONS: { id: Visibility; label: string }[] = [
  { id: "private", label: "自分のみ" },
  { id: "class", label: "クラス" },
  { id: "school", label: "学校" },
  { id: "public", label: "公開" },
];

export default function CreateScreen() {
  const [body, setBody] = useState("");
  const [reflection, setReflection] = useState("");
  const [mediaUris, setMediaUris] = useState<string[]>([]);
  const [isPosting, setIsPosting] = useState(false);
  const [discoveryType, setDiscoveryType] = useState(DISCOVERY_TYPES[0]);
  const [visibility, setVisibility] = useState<Visibility>("class");
  const [selectedCardIds, setSelectedCardIds] = useState<string[]>([]);
  const [selectedSubjectId, setSelectedSubjectId] = useState<string | null>(
    null,
  );
  const {
    addPost,
    cards,
    currentUser,
    login,
    recordCardUsage,
    subjects,
  } = useAppStore();
  const c = useColors();
  const styles = useMemo(() => createStyles(c), [c]);
  const router = useRouter();
  const { cardId: preselectCardId } = useLocalSearchParams<{
    cardId?: string;
  }>();

  // カード詳細から「このレンズで投稿」で遷移してきたとき、対象レンズを事前選択する
  useEffect(() => {
    if (!preselectCardId) return;
    const card = cards.find((c) => c.id === preselectCardId);
    if (!card) return;
    setSelectedCardIds((current) =>
      current.includes(card.id) ? current : [...current, card.id],
    );
    setSelectedSubjectId(card.subject_id);
    // 適用済みパラメータをクリアし、タブ再表示時に再選択されないようにする
    router.setParams({ cardId: "" });
  }, [preselectCardId, cards, router]);

  // フィルタリングされたカード
  const filteredCards = selectedSubjectId
    ? cards.filter((card) => card.subject_id === selectedSubjectId)
    : cards;

  const handlePost = async () => {
    const normalizedBody = body.trim();
    const normalizedReflection = reflection.trim();

    if (!normalizedBody || selectedCardIds.length === 0) {
      Alert.alert("入力が必要です", "発見の説明と使ったレンズを選択してください。");
      return;
    }

    setIsPosting(true);
    try {
      await addPost({
        body: normalizedBody,
        reflection: normalizedReflection || undefined,
        discovery_type: discoveryType,
        visibility,
        media_urls: mediaUris.length > 0 ? mediaUris : undefined,
        card_ids: selectedCardIds,
      });

      await Promise.all(
        selectedCardIds.map(async (cardId) => {
          const card = cards.find((c) => c.id === cardId);
          if (card) {
            await recordCardUsage(cardId, card.subject_id);
          }
        }),
      );

      setBody("");
      setReflection("");
      setMediaUris([]);
      setDiscoveryType(DISCOVERY_TYPES[0]);
      setVisibility("class");
      setSelectedCardIds([]);
      setSelectedSubjectId(null);
      router.navigate("/(tabs)");
    } catch (error) {
      console.warn("Failed to create post", error);
      Alert.alert(
        "投稿できませんでした",
        mediaUris.length > 0
          ? "写真のアップロードに失敗しました。通信状態を確認して、もう一度お試しください。"
          : "通信状態を確認して、もう一度お試しください。",
      );
    } finally {
      setIsPosting(false);
    }
  };

  const toggleCard = (id: string) => {
    if (selectedCardIds.includes(id)) {
      setSelectedCardIds(selectedCardIds.filter((cardId) => cardId !== id));
    } else {
      setSelectedCardIds([...selectedCardIds, id]);
    }
  };

  const getSelectedCardsSummary = () => {
    const selectedCards = cards.filter((c) =>
      selectedCardIds.includes(c.id),
    );
    return selectedCards.reduce((acc: { [key: string]: number }, card) => {
      acc[card.subject_id] = (acc[card.subject_id] || 0) + 1;
      return acc;
    }, {});
  };

  const selectedSummary = getSelectedCardsSummary();

  const pickMedia = async () => {
    try {
      const permission = await ImagePicker.requestMediaLibraryPermissionsAsync();
      if (!permission.granted) {
        Alert.alert(
          "写真へのアクセスが必要です",
          "端末の設定から写真ライブラリへのアクセスを許可してください。",
        );
        return;
      }

      const result = await ImagePicker.launchImageLibraryAsync({
        mediaTypes: ImagePicker.MediaTypeOptions.Images,
        allowsMultipleSelection: true,
        quality: 0.85,
        selectionLimit: 4,
      });

      if (result.canceled) return;

      const selectedUris = result.assets.map((asset) => asset.uri);
      setMediaUris((currentUris) =>
        [...currentUris, ...selectedUris].slice(0, 4),
      );
    } catch (error) {
      console.warn("Failed to pick media", error);
      Alert.alert(
        "写真を選択できませんでした",
        "時間をおいてもう一度お試しください。",
      );
    }
  };

  if (!currentUser) {
    return (
      <View style={styles.loginContainer}>
        <Text variant="headlineSmall" style={styles.loginTitle}>
          ログインが必要です
        </Text>
        <Text style={styles.loginText}>
          投稿は学習記録としてプロフィールとマイレンズに保存されます。
        </Text>
        {isSupabaseConfigured ? (
          <Link href="/profile" asChild>
            <Pressable accessibilityRole="button" style={styles.loginLinkButton}>
              <Text style={styles.loginLinkText}>ログイン画面へ</Text>
            </Pressable>
          </Link>
        ) : (
          <Button
            mode="contained"
            onPress={() => {
              login().catch((error) => {
                console.warn("Failed to use demo login", error);
                Alert.alert("ログインできませんでした", "もう一度お試しください。");
              });
            }}
            style={styles.loginButton}
          >
            体験ログイン
          </Button>
        )}
      </View>
    );
  }

  return (
    <ScrollView style={styles.container} keyboardShouldPersistTaps="handled">
      <View style={styles.content}>
        {/* ヘッダー */}
        <View style={styles.header}>
          <Text variant="headlineSmall" style={styles.title}>
            ✨ 新しい発見を記録
          </Text>
          <Text variant="bodySmall" style={styles.subtitle}>
            日常の中で見つけたことを、学問レンズで整理しましょう
          </Text>
        </View>

        {/* 発見の説明 */}
        <View style={styles.section}>
          <Text variant="titleMedium" style={styles.label}>
            📝 発見したこと（テンプレート）
          </Text>
          <Text variant="bodySmall" style={styles.hint}>
            「今日見つけたもの」「そう考えた理由」「新しく気づいたこと」などを書いてみてください
          </Text>
          <TextInput
            mode="outlined"
            multiline
            numberOfLines={6}
            value={body}
            onChangeText={setBody}
            placeholder="例：
今日見つけたもの：コンビニの店員さん
そう考えた理由：...
新しく気づいたこと：..."
            style={styles.input}
          />
        </View>

        <View style={styles.section}>
          <Text variant="titleMedium" style={styles.label}>
            発見タイプ
          </Text>
          <View style={styles.optionGrid}>
            {DISCOVERY_TYPES.map((type) => (
              <Pressable
                key={type}
                accessibilityRole="button"
                onPress={() => setDiscoveryType(type)}
                style={[
                  styles.optionChip,
                  discoveryType === type && styles.optionChipActive,
                ]}
              >
                {discoveryType === type && <Check size={15} color={c.primaryDark} />}
                <Text
                  style={[
                    styles.optionText,
                    discoveryType === type && styles.optionTextActive,
                  ]}
                >
                  {type}
                </Text>
              </Pressable>
            ))}
          </View>
        </View>

        <View style={styles.section}>
          <Text variant="titleMedium" style={styles.label}>
            写真
          </Text>
          <Pressable
            accessibilityRole="button"
            onPress={pickMedia}
            style={styles.mediaPicker}
          >
            <ImagePlus size={22} color={c.primary} />
            <Text style={styles.mediaPickerText}>端末から選択</Text>
          </Pressable>
          {mediaUris.length > 0 && (
            <View style={styles.mediaPreviewGrid}>
              {mediaUris.map((uri) => (
                <View key={uri} style={styles.mediaPreviewItem}>
                  <Image source={{ uri }} style={styles.mediaPreview} />
                  <Pressable
                    accessibilityRole="button"
                    onPress={() =>
                      setMediaUris((currentUris) =>
                        currentUris.filter((item) => item !== uri),
                      )
                    }
                    style={styles.removeMediaButton}
                  >
                    <X size={14} color="#fff" />
                  </Pressable>
                </View>
              ))}
            </View>
          )}
        </View>

        <View style={styles.section}>
          <Text variant="titleMedium" style={styles.label}>
            公開範囲
          </Text>
          <View style={styles.optionGrid}>
            {VISIBILITY_OPTIONS.map((option) => (
              <Pressable
                key={option.id}
                accessibilityRole="button"
                onPress={() => setVisibility(option.id)}
                style={[
                  styles.optionChip,
                  visibility === option.id && styles.optionChipActive,
                ]}
              >
                {visibility === option.id && <Check size={15} color={c.primaryDark} />}
                <Text
                  style={[
                    styles.optionText,
                    visibility === option.id && styles.optionTextActive,
                  ]}
                >
                  {option.label}
                </Text>
              </Pressable>
            ))}
          </View>
        </View>

        {/* 使ったレンズの選択 */}
        <View style={styles.section}>
          <View style={styles.labelRow}>
            <Text variant="titleMedium" style={styles.label}>
              🔍 使った学問レンズ
            </Text>
            {Object.keys(selectedSummary).length > 0 && (
              <Text style={styles.selectedCount}>
                {selectedCardIds.length}枚選択
              </Text>
            )}
          </View>

          {/* 科目フィルタ */}
          <Text variant="bodySmall" style={styles.hint}>
            科目を選択してカードを絞り込む
          </Text>
          <ScrollView
            horizontal
            showsHorizontalScrollIndicator={false}
            style={styles.subjectFilter}
            contentContainerStyle={styles.subjectFilterContent}
          >
            <Pressable
              accessibilityRole="button"
              onPress={() => setSelectedSubjectId(null)}
              style={[
                styles.subjectChip,
                selectedSubjectId === null && styles.subjectChipActive,
              ]}
            >
              {selectedSubjectId === null && <Check size={15} color={c.textPrimary} />}
              <Text style={styles.subjectChipText}>すべて</Text>
            </Pressable>
            {subjects.map((subject) => (
              <Pressable
                key={subject.id}
                accessibilityRole="button"
                onPress={() => setSelectedSubjectId(subject.id)}
                style={[
                  styles.subjectChip,
                  { borderColor: subject.color_code },
                  selectedSubjectId === subject.id && {
                    backgroundColor: `${subject.color_code}14`,
                  },
                ]}
              >
                {selectedSubjectId === subject.id && (
                  <Check size={15} color={c.textPrimary} />
                )}
                <Text
                  style={[styles.subjectChipText, { color: subject.color_code }]}
                >
                  {subject.name}
                </Text>
              </Pressable>
            ))}
          </ScrollView>

          {/* カード選択 */}
          <View style={styles.cardsGrid}>
            {filteredCards.map((card) => {
              const subject = subjects.find((s) => s.id === card.subject_id);
              const isSelected = selectedCardIds.includes(card.id);
              return (
                <Pressable
                  key={card.id}
                  accessibilityRole="button"
                  onPress={() => toggleCard(card.id)}
                  style={[
                    styles.cardChip,
                    isSelected && styles.cardChipSelected,
                    { borderColor: subject?.color_code || c.border },
                  ]}
                >
                  {isSelected && <Check size={14} color={c.primaryDark} />}
                  <Text style={styles.cardChipText}>{card.title}</Text>
                </Pressable>
              );
            })}
          </View>
          {cards.length === 0 && (
            <View style={styles.emptyLensBox}>
              <Text style={styles.emptyLensText}>
                学問レンズはまだ登録されていません
              </Text>
              <Text style={styles.emptyLensSubtext}>
                管理サイトで追加されたレンズを使って投稿できます
              </Text>
            </View>
          )}

          {/* 選択されたカード一覧 */}
          {selectedCardIds.length > 0 && (
            <View style={styles.selectedCardsPreview}>
              <Text variant="bodySmall" style={styles.previewLabel}>
                選択済み：
              </Text>
              <View style={styles.selectedCardsList}>
                {cards.filter((c) => selectedCardIds.includes(c.id)).map(
                  (card) => {
                    const subject = subjects.find(
                      (s) => s.id === card.subject_id,
                    );
                    return (
                      <View
                        key={card.id}
                        style={[
                          styles.selectedCard,
                          { backgroundColor: subject?.color_code + "20" },
                        ]}
                      >
                        <Text
                          style={[
                            styles.selectedCardText,
                            { color: subject?.color_code },
                          ]}
                        >
                          {card.title}
                        </Text>
                      </View>
                    );
                  },
                )}
              </View>
            </View>
          )}
        </View>

        {/* 振り返りメモ */}
        <View style={styles.section}>
          <Text variant="titleMedium" style={styles.label}>
            💭 振り返りメモ
          </Text>
          <Text variant="bodySmall" style={styles.hint}>
            次に調べたいことなど、気づいたことを自由に書きましょう
          </Text>
          <TextInput
            mode="outlined"
            multiline
            numberOfLines={3}
            value={reflection}
            onChangeText={setReflection}
            placeholder="例：他の店員さんの対応を観察してみたい..."
            style={styles.input}
          />
        </View>

        {/* 投稿ボタン */}
        <View style={styles.buttonContainer}>
          <Button
            mode="contained"
            onPress={handlePost}
            loading={isPosting}
            disabled={!body.trim() || selectedCardIds.length === 0 || isPosting}
            style={styles.postButton}
            buttonColor={c.primary}
          >
            ✨ 発見を投稿
          </Button>
        </View>

        <View style={{ height: 30 }} />
      </View>
    </ScrollView>
  );
}

const createStyles = (c: ThemeColors) => StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: c.bg,
  },
  loginContainer: {
    flex: 1,
    alignItems: "center",
    justifyContent: "center",
    padding: 24,
    backgroundColor: c.bg,
  },
  loginTitle: {
    fontWeight: "700",
    marginBottom: 10,
    color: c.textPrimary,
  },
  loginText: {
    color: c.textSecondary,
    lineHeight: 22,
    textAlign: "center",
  },
  loginButton: {
    marginTop: 18,
    minWidth: 160,
  },
  loginLinkButton: {
    minWidth: 160,
    minHeight: 44,
    marginTop: 18,
    borderRadius: 22,
    backgroundColor: c.primary,
    alignItems: "center",
    justifyContent: "center",
    paddingHorizontal: 18,
  },
  loginLinkText: {
    color: "#fff",
    fontWeight: "700",
  },
  content: {
    paddingHorizontal: 16,
    paddingVertical: 16,
  },
  header: {
    marginBottom: 24,
    paddingHorizontal: 12,
  },
  title: {
    fontWeight: "700",
    marginBottom: 4,
    color: c.textPrimary,
  },
  subtitle: {
    color: c.textSecondary,
    marginTop: 4,
  },
  section: {
    marginBottom: 24,
    backgroundColor: c.surface,
    borderRadius: 12,
    padding: 16,
  },
  label: {
    fontWeight: "700",
    marginBottom: 4,
    color: c.textPrimary,
  },
  labelRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 4,
  },
  selectedCount: {
    color: c.primaryDark,
    fontWeight: "700",
    fontSize: 12,
    backgroundColor: c.primarySoft,
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
  },
  hint: {
    color: c.textMuted,
    marginBottom: 12,
    lineHeight: 16,
  },
  input: {
    backgroundColor: c.surfaceAlt,
  },
  mediaPicker: {
    height: 48,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: c.primary,
    borderStyle: "dashed",
    backgroundColor: c.primarySoft,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
    marginTop: 10,
  },
  mediaPickerText: {
    color: c.primaryDark,
    fontWeight: "700",
  },
  mediaPreviewGrid: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 8,
    marginTop: 12,
  },
  mediaPreviewItem: {
    width: "48%",
    aspectRatio: 1,
  },
  mediaPreview: {
    width: "100%",
    height: "100%",
    borderRadius: 10,
    backgroundColor: c.surfaceAlt,
  },
  removeMediaButton: {
    position: "absolute",
    top: 6,
    right: 6,
    width: 24,
    height: 24,
    borderRadius: 12,
    backgroundColor: "rgba(0,0,0,0.65)",
    alignItems: "center",
    justifyContent: "center",
  },
  optionGrid: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 8,
    marginTop: 10,
  },
  optionChip: {
    minHeight: 38,
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    paddingHorizontal: 12,
    borderRadius: 19,
    borderWidth: 1,
    borderColor: c.border,
    backgroundColor: c.surface,
  },
  optionChipActive: {
    borderColor: c.primary,
    backgroundColor: c.primarySoft,
  },
  optionText: {
    color: c.textSecondary,
    fontSize: 13,
    fontWeight: "700",
  },
  optionTextActive: {
    color: c.primaryDark,
  },
  subjectFilter: {
    marginBottom: 16,
    flexGrow: 0,
  },
  subjectFilterContent: {
    gap: 8,
    alignItems: "center",
  },
  subjectChip: {
    minWidth: 76,
    height: 36,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: 6,
    paddingHorizontal: 12,
    borderRadius: 20,
    borderWidth: 1,
    borderColor: c.border,
    backgroundColor: c.surface,
  },
  subjectChipActive: {
    backgroundColor: c.primarySoft,
    borderColor: c.primary,
  },
  subjectChipText: {
    color: c.textPrimary,
    fontSize: 13,
    fontWeight: "700",
  },
  cardsGrid: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 8,
    marginBottom: 12,
  },
  emptyLensBox: {
    marginBottom: 12,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: c.border,
    backgroundColor: c.surfaceAlt,
    padding: 14,
  },
  emptyLensText: {
    color: c.textPrimary,
    fontWeight: "700",
    marginBottom: 4,
  },
  emptyLensSubtext: {
    color: c.textMuted,
    fontSize: 13,
    lineHeight: 18,
  },
  cardChip: {
    minHeight: 36,
    flexDirection: "row",
    alignItems: "center",
    gap: 5,
    borderWidth: 1,
    borderRadius: 18,
    paddingHorizontal: 12,
    paddingVertical: 8,
    marginBottom: 8,
    backgroundColor: c.surfaceAlt,
  },
  cardChipSelected: {
    backgroundColor: c.primarySoft,
  },
  cardChipText: {
    fontSize: 12,
    color: c.textPrimary,
  },
  selectedCardsPreview: {
    marginTop: 12,
    paddingTop: 12,
    borderTopWidth: 1,
    borderTopColor: c.border,
  },
  previewLabel: {
    color: c.textSecondary,
    marginBottom: 8,
  },
  selectedCardsList: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 6,
  },
  selectedCard: {
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 8,
  },
  selectedCardText: {
    fontSize: 12,
    fontWeight: "600",
  },
  buttonContainer: {
    gap: 12,
  },
  postButton: {
    paddingVertical: 6,
  },
});
